import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';

import { selectTenantHasFeatureFlag, selectUserHasEitherPermissions } from '@core/stores/auth/auth.selectors';

import { PermissionStoreModelEnum } from '@domain/permissions/permissions.enums';
import { FeatureFlags } from '@domain/tenant/tenant.enums';

import { LoadingSkeletonComponent } from '@shared/components/loading-skeleton';
import { ScrollableStickyDirective } from '@shared/directives/scrollable-sticky';
import { DialogModule } from '@shared/modules/dialog';
import { UpPipe } from '@shared/pipes/up';

import { AiAssistantBannerComponent } from './components/ai-assistant/ai-assistant-banner.component';
import { AiAssistantPanelComponent } from './components/ai-assistant/ai-assistant-panel.component';
import { BreadcrumbsComponent } from './components/breadcrumbs';
import { FooterComponent } from './components/footer';
import { HeaderProfileSectionComponent } from './components/header-profile-section';
import { QuickNavigationService } from './components/quick-navigation/quick-navigation.service';
import { SideMenuComponent } from './components/side-menu';
import { LayoutService } from './layout.service';

@Component({
  selector: 'app-layout',
  imports: [
    RouterOutlet,
    SideMenuComponent,
    BreadcrumbsComponent,
    AsyncPipe,
    HeaderProfileSectionComponent,
    LoadingSkeletonComponent,
    FooterComponent,
    UpPipe,
    DialogModule,
    NgTemplateOutlet,
    AiAssistantBannerComponent,
    AiAssistantPanelComponent,
    ScrollableStickyDirective,
  ],
  providers: [LayoutService],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent {
  readonly #layoutService = inject(LayoutService);
  readonly #quickNavigationService = inject(QuickNavigationService);
  readonly #store = inject(Store);

  public readonly hasAiPermission = this.#store.selectSignal(
    selectUserHasEitherPermissions([
      PermissionStoreModelEnum.ViewAnySiteWithAiAssistant,
      PermissionStoreModelEnum.ViewOwnSiteWithAiAssistant,
    ])
  );
  public readonly hasAiAssistantEnabled = this.#store.selectSignal(selectTenantHasFeatureFlag(FeatureFlags.AiAssistant));

  public readonly sideMenuVm = this.#layoutService.sideMenuVm;
  public readonly breadcrumbsVm = this.#layoutService.breadcrumbsVm;
  public readonly title$Signal = this.#layoutService.title$Signal;
  public readonly subtitle$Signal = this.#layoutService.customSubtitle$Signal;
  public readonly subtitleIsLoading = computed(() => this.#layoutService.customSubtitleIsLoadingSignal()?.() ?? false);
  public readonly headerProfileVm = this.#layoutService.headerProfileVm;
  public readonly customTitleTemplateSignal = this.#layoutService.customTitleTemplateSignal;

  public onSideMenuExpandChange(expanded: boolean): void {
    this.#layoutService.onSideMenuExpandChange(expanded);
  }

  public onQuickNavigationClick(): void {
    this.#quickNavigationService.open();
  }

  public onLogoutClick(): void {
    this.#layoutService.onLogoutClick();
  }

  public onExitImpersonationClick(): void {
    this.#layoutService.onExitImpersonationClick();
  }
}
